import { useCallback, useRef, useState } from 'react';
import { usePromise } from 'react-use';
import { useDebounce } from 'use-debounce';

interface ITrackedPromise<T> {
    /**
     * The promise that is being tracked
     */
    promise: Promise<T>;

    /**
     * Abandon tracking of this promise
     */
    abandon: () => void;
}

type TrackPromiseHandler = <T>(promiseFn: () => Promise<T>, message?: string) => ITrackedPromise<T>;

interface IPromiseTrackerStatus {
    isBusy: boolean;
    status: string | undefined;
    trackPromise: TrackPromiseHandler;
}

export default function usePromiseTracker(debounceMillis = 400): IPromiseTrackerStatus {
    const resolveIfMounted = usePromise();

    const promisesRef = useRef([] as { promise: Promise<unknown>; message: string | undefined }[]);
    const [busyMessage, setBusyMessage] = useState<string | null>(null);
    const [busyMessageDebounced] = useDebounce(busyMessage, debounceMillis);

    const updateBusyMessage = useCallback(() => {
        if (promisesRef.current.length === 0) {
            setBusyMessage(null);
            return;
        }
        const firstEntryWithMessage = promisesRef.current.find(entry => !!entry.message);
        setBusyMessage(firstEntryWithMessage?.message ?? 'Busy');
    }, []);

    const remove = useCallback(
        (promise: Promise<unknown>) => {
            promisesRef.current = promisesRef.current.filter(entry => entry.promise !== promise);
            updateBusyMessage();
        },
        [updateBusyMessage]
    );

    const add = useCallback(
        (promise: Promise<unknown>, message?: string) => {
            if (!(promise && promise.then)) return;

            const removeThisPromise = () => remove(promise);
            resolveIfMounted(promise).then(removeThisPromise).catch(removeThisPromise);

            promisesRef.current = [...promisesRef.current, { promise, message }];
            updateBusyMessage();
        },
        [resolveIfMounted, remove, updateBusyMessage]
    );

    const track: TrackPromiseHandler = useCallback(
        (promiseFn, message) => {
            const promise = promiseFn();
            add(promise, message);

            return {
                promise,
                abandon: () => remove(promise),
            };
        },
        [add, remove]
    );

    return {
        isBusy: !!(busyMessage && busyMessageDebounced),
        status: (busyMessage && busyMessageDebounced) ?? undefined,
        trackPromise: track,
    };
}
